/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, August 23rd 2022, 1:41:27 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { WorkerAccount } from "lib/schemas";
import { useAPIChangeWorkerType } from "workers/services/WorkersAPI";
import { ActionDialog, useToggleState, AlertSnackBar } from "ui-widgets";
import { useGlobalScope } from "core/providers";

interface ChangeWorkerTypeProps {
  workerId: WorkerAccount["id"];
  workerName: string;
  workerType: WorkerAccount["worker_type"];
  open: boolean;
  handleClose: () => void;
  handleUpdatedWorkerFromAPI?: (apiResponse: WorkerAccount) => void;
}

const ChangeWorkerType = ({
  workerId,
  workerName,
  handleClose,
  open,
  handleUpdatedWorkerFromAPI,
  workerType,
}: ChangeWorkerTypeProps) => {
  const { patchData, isSuccessfulAPIRequest } = useAPIChangeWorkerType();

  const { handleClose: handleCloseSuccess, handleOpen: handleOpenSuccess, open: openSuccess } = useToggleState();

  const changeWorkerType = (worker_type: WorkerAccount["worker_type"]) => {
    patchData({
      apiPathParams: [workerId],
      body: JSON.stringify({ worker_type }),
      setValidAPIResponse: (apiResponse: WorkerAccount) => {
        handleUpdatedWorkerFromAPI?.(apiResponse);
        handleOpenSuccess();
      },
    });
    handleClose();
  };

  let warningText: React.ReactNode;
  let warningTitle: string;
  let successMessage: string;
  let workerTypeToChangeTo: WorkerAccount["worker_type"];

  switch (workerType) {
    case "WORKER":
      warningText = (
        <>
          <p>
            You are about to make {workerName} an administrator with access to the admin portal for your organization.
          </p>
          <p>Are you sure about this?</p>
        </>
      );
      warningTitle = `Make ${workerName} an administrator`;
      successMessage = isSuccessfulAPIRequest && `${workerName} is no longer an administrator`;
      workerTypeToChangeTo = "ADMINISTRATOR";
      break;
    case "ADMINISTRATOR":
      warningText = (
        <>
          <p>
            You are about to dismiss {workerName} an administrator. They will no longer have access to the admin portal
            for your organization.
          </p>
          <p>Are you sure about this?</p>
        </>
      );
      warningTitle = `Dismiss ${workerName} as an administrator`;
      successMessage = isSuccessfulAPIRequest && `${workerName} is now an administrator`;
      workerTypeToChangeTo = "WORKER";
  }

  return (
    <>
      {isSuccessfulAPIRequest && (
        <AlertSnackBar
          open={openSuccess}
          message={successMessage}
          handleClose={handleCloseSuccess}
          severity="success"
        />
      )}
      <ActionDialog
        severity="warning"
        title={warningTitle}
        open={open}
        onClose={handleClose}
        confirmationAction={() => changeWorkerType(workerTypeToChangeTo)}
        dialogMessage={warningText}
      />
    </>
  );
};

interface ChangeWorkerTypeButtonWrapperProps
  extends Pick<ChangeWorkerTypeProps, "workerId" | "workerName" | "workerType" | "handleUpdatedWorkerFromAPI"> {
  children: any;
}

export const ChangeWorkerTypeButtonWrapper = ({
  children,
  workerId,
  workerName,
  workerType,
  handleUpdatedWorkerFromAPI,
}: ChangeWorkerTypeButtonWrapperProps) => {
  const { handleClose, handleOpen, open } = useToggleState();
  return (
    <>
      {React.Children.map(children, (child) => React.cloneElement(child, { onClick: handleOpen }))}
      <ChangeWorkerType
        handleClose={handleClose}
        open={open}
        workerId={workerId}
        workerName={workerName}
        workerType={workerType}
        handleUpdatedWorkerFromAPI={handleUpdatedWorkerFromAPI}
      />
    </>
  );
};

interface ChangeWorkerTypeButtonProps extends Pick<ChangeWorkerTypeButtonWrapperProps, "handleUpdatedWorkerFromAPI"> {
  worker: WorkerAccount;
}

export const ChangeWorkerTypeButton = ({ worker, handleUpdatedWorkerFromAPI }: ChangeWorkerTypeButtonProps) => {
  let tooltipText: string;
  let icon: React.ReactNode;
  switch (worker.worker_type) {
    case "WORKER":
      tooltipText = `Make ${worker.first_name} an Administrator`;
      icon = <AdminPanelSettingsIcon color="secondary" />;
      break;
    case "ADMINISTRATOR":
      tooltipText = `Dismiss ${worker.first_name} as an Administrator`;
      icon = <PersonRemoveIcon color="secondary" />;
  }

  const {
    userContext: { loggedInUser },
  } = useGlobalScope();

  const hideChangeWorkerTypeButton = loggedInUser?.id === worker.id || worker.is_super_user;

  return hideChangeWorkerTypeButton ? null : (
    <ChangeWorkerTypeButtonWrapper
      workerId={worker.id}
      workerName={worker.first_name}
      workerType={worker.worker_type}
      handleUpdatedWorkerFromAPI={handleUpdatedWorkerFromAPI}
    >
      <Tooltip title={<Typography>{tooltipText}</Typography>}>
        <IconButton>{icon}</IconButton>
      </Tooltip>
    </ChangeWorkerTypeButtonWrapper>
  );
};
